import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatusChip, EmptyState } from "@/components/Common";
import { toast } from "sonner";

const EMPTY = { product_id: "", movement_type: "entrada", quantity: 1, unit_cost: 0, reference: "", notes: "" };

export default function Kardex() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState("");
  const [report, setReport] = useState(null);
  const [movements, setMovements] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  useEffect(() => {
    if (!selected) {
      api.get("/kardex/movements").then((r) => { setMovements(r.data); setReport(null); });
      return;
    }
    api.get(`/kardex/report/${selected}`).then((r) => {
      setReport(r.data);
      setMovements(r.data.movements.slice().reverse());
    });
  }, [selected]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/kardex/movements", {
        ...form,
        quantity: Number(form.quantity),
        unit_cost: Number(form.unit_cost) || 0,
      });
      toast.success("Movimiento registrado");
      setForm(EMPTY); setShowForm(false);
      // reload
      if (selected) {
        const r = await api.get(`/kardex/report/${selected}`);
        setReport(r.data); setMovements(r.data.movements.slice().reverse());
      } else {
        const r = await api.get("/kardex/movements"); setMovements(r.data);
      }
      api.get("/products").then((r) => setProducts(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

  return (
    <div data-testid="kardex-page">
      <PageHeader
        title="Kardex de Inventario"
        subtitle="Entradas, salidas y ajustes con valuación FIFO o promedio ponderado."
        testid="kardex-header"
        action={
          <button data-testid="kardex-create-movement"
            onClick={() => { setForm({ ...EMPTY, product_id: selected || "" }); setShowForm(true); }}
            className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nuevo movimiento
          </button>
        }
      />

      <div className="card-soft p-5 mb-6">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Filtrar por producto</span>
          <select data-testid="kardex-product-filter" className="input-soft max-w-md" value={selected} onChange={(e) => setSelected(e.target.value)}>
            <option value="">Todos los productos</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
          </select>
        </label>

        {report && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
            <div><p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">Producto</p><p className="font-bold">{report.product.name}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">SKU</p><p className="font-mono">{report.product.sku}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">Stock actual</p><p className="font-heading text-2xl font-black">{Number(report.product.stock || 0).toFixed(2)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">Unidad</p><p>{report.product.unit}</p></div>
          </div>
        )}
      </div>

      {showForm && (
        <div data-testid="kardex-form" className="card-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">Nuevo movimiento de kardex</h3>
            <button onClick={() => setShowForm(false)} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block md:col-span-2">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Producto *</span>
              <select data-testid="kardex-product-select" required className="input-soft" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
                <option value="">Seleccione</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku}) – stock: {Number(p.stock || 0).toFixed(2)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Tipo *</span>
              <select data-testid="kardex-movement-type" className="input-soft" value={form.movement_type} onChange={(e) => setForm({ ...form, movement_type: e.target.value })}>
                <option value="entrada">Entrada</option>
                <option value="salida">Salida</option>
                <option value="ajuste">Ajuste</option>
              </select>
            </label>
            <Field label="Cantidad *"><input data-testid="kardex-quantity" type="number" step="0.01" required className="input-soft" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Field>
            <Field label="Costo unitario"><input type="number" step="0.0001" className="input-soft" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></Field>
            <Field label="Referencia"><input className="input-soft" value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></Field>
            <div className="md:col-span-2"><Field label="Notas"><textarea rows={2} className="input-soft" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button data-testid="kardex-save-button" className="btn-primary" type="submit">Registrar</button>
            </div>
          </form>
        </div>
      )}

      {movements.length === 0 ? (
        <EmptyState title="Sin movimientos" hint="Registra la primera entrada para iniciar el kardex." testid="kardex-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="kardex-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
                <th className="px-5 py-3">Fecha</th><th className="px-5 py-3">Producto</th>
                <th className="px-5 py-3">Tipo</th><th className="px-5 py-3 text-right">Cantidad</th>
                <th className="px-5 py-3 text-right">Costo unit.</th><th className="px-5 py-3 text-right">Saldo</th>
                <th className="px-5 py-3">Método</th><th className="px-5 py-3">Referencia</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((m) => (
                <tr key={m.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-4 text-xs text-[#5C6B64]">{new Date(m.created_at).toLocaleString()}</td>
                  <td className="px-5 py-4">{productMap[m.product_id]?.name || m.product_id}</td>
                  <td className="px-5 py-4"><StatusChip status={m.movement_type} /></td>
                  <td className="px-5 py-4 text-right font-medium">{Number(m.quantity).toFixed(2)}</td>
                  <td className="px-5 py-4 text-right">$ {Number(m.unit_cost).toFixed(4)}</td>
                  <td className="px-5 py-4 text-right font-bold">{Number(m.balance_qty).toFixed(2)}</td>
                  <td className="px-5 py-4 uppercase text-xs tracking-wider">{m.valuation_method}</td>
                  <td className="px-5 py-4 text-xs text-[#5C6B64]">{m.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
